import { MessageDto } from '@act/shared/data-transfer-objects';

import { FormBuilder, SelectOption } from '@act/storybook';
import { Alert, Box } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CompositeDialog from 'react-components/dialog/CompositeDialog';

import { TrackingProvider, useTracking } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { visitDateTimeHelpers } from 'utilities/dateTimeGenerator.util';
import { useSchedulingStatus } from '../hooks/useSchedulingStatus.hook';

interface MessageForm {
  phoneNumberId: number;
  message: string;
}

interface ScheduleMessageDialogProps {
  message?: MessageDto;
  handleClose: () => void;
  handleScheduleMessage: (
    data: MessageForm | { scheduleDate: Date; scheduleTime: number }
  ) => void;
  isOpen: boolean;
}

export default function ScheduleMessageDialog({
  message,
  handleClose,
  isOpen,
  handleScheduleMessage,
}: ScheduleMessageDialogProps) {
  const { generateTrackingProps } = useTracking();
  const [selectedDate, setSelectedDate] = useState<Date>(
    message?.sendAt ? new Date(message.sendAt) : new Date()
  );

  const startTimeOptions: SelectOption<number>[] = useMemo(() => {
    // If editing a message, use its sendAt date, otherwise use today
    const baseDate = message?.sendAt ? message.sendAt : new Date().getTime();

    // Create time options for the specific date
    // Constrain time to 8am - 6pm
    const startOfDay = DateTime.fromMillis(baseDate)
      .startOf('day')
      .plus({ hours: 8 });
    const endOfDay = startOfDay.plus({ hours: 10 });

    const timeOptions: SelectOption<number>[] = [];
    let cursor = startOfDay;

    while (cursor < endOfDay) {
      timeOptions.push({
        label: cursor.toFormat('h:mm a'),
        value: visitDateTimeHelpers.getTimeValue(cursor.toMillis()),
      });
      cursor = cursor.plus({ minutes: 15 });
    }

    return timeOptions;
  }, [message?.sendAt]);
  const defaultStartTime: number | string = useMemo(() => {
    if (message?.sendAt) {
      // If editing a message, use its time value
      return visitDateTimeHelpers.getTimeValue(message.sendAt);
    }

    // For new messages, get the time that is most recent
    const now = visitDateTimeHelpers.getTimeValue(new Date().getTime());
    for (let i = 0; i < startTimeOptions.length; i++) {
      if ((startTimeOptions[i].value as number) > now) {
        if (i === 0) {
          return startTimeOptions[0].value;
        } else {
          return startTimeOptions[i - 1].value;
        }
      }
    }
    return startTimeOptions[startTimeOptions.length - 1].value;
  }, [startTimeOptions, message?.sendAt]);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      scheduleDate: message?.sendAt ? new Date(message.sendAt) : new Date(),
      scheduleTime: message?.sendAt
        ? visitDateTimeHelpers.getTimeValue(message.sendAt)
        : defaultStartTime,
    },
  });

  // Watch for date changes to update scheduling status
  const watchedDate = watch('scheduleDate');

  // Memoize the date validation function to prevent recreating it on every render
  const getValidDate = useCallback((date: any): Date => {
    if (date instanceof Date) {
      return date;
    }
    if (typeof date === 'string') {
      // Handle YYYY-MM-DD format from form
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const parsedDate = new Date(date + 'T00:00:00'); // Add time to avoid timezone issues
        return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
      }
      // Handle other string formats
      const parsedDate = new Date(date);
      return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    }
    return new Date();
  }, []);

  // Debounce date changes to prevent excessive API calls
  const [debouncedDate, setDebouncedDate] = useState<Date | null>(null);

  // Memoize the default date to prevent creating new Date objects on every render
  const defaultDate = useMemo(() => new Date(), []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedDate(getValidDate(watchedDate || selectedDate));
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [watchedDate, selectedDate, getValidDate]);

  const { schedulingStatus, isLoading: isLoadingStatus } = useSchedulingStatus(
    debouncedDate || defaultDate
  );

  useEffect(() => {
    if (watchedDate) {
      setSelectedDate(watchedDate);
    }
  }, [watchedDate]);

  const handleSubmitForm = (data: any) => {
    // Check if scheduling is allowed
    if (schedulingStatus && !schedulingStatus.canSchedule) {
      return; // Don't submit if scheduling is not allowed
    }
    if (schedulingStatus && !schedulingStatus.canSend) {
      return; // Don't submit if sending is not allowed
    }

    handleScheduleMessage(data);
    handleClose();
  };

  // Determine if scheduling is disabled
  const isSchedulingDisabled =
    isLoadingStatus ||
    (schedulingStatus && !schedulingStatus.canSchedule) ||
    (schedulingStatus && !schedulingStatus.canSend);

  return (
    <TrackingProvider contextName="schedule-message-dialog">
      <CompositeDialog
        open={isOpen}
        handleClose={handleClose}
        title="Schedule text message"
        actions={[
          {
            text: 'Cancel',
            callback: handleClose,
            ...generateTrackingProps('cancel-button'),
          },
          {
            text: 'Schedule Text',
            type: 'submit',
            isPrimary: true,
            callback: handleSubmit(handleSubmitForm),
            disabled: isSchedulingDisabled,
            ...generateTrackingProps('submit-button'),
          },
        ]}
      >
        {/* Scheduling Status Information */}
        {schedulingStatus && (
          <Box sx={{ marginBottom: 2 }}>
            {!schedulingStatus.canSchedule && (
              <Alert severity="error" sx={{ marginBottom: 1 }}>
                Cannot schedule more messages: You have reached the daily limit
                of {schedulingStatus.scheduledLimit} scheduled messages per day.
              </Alert>
            )}

            {!schedulingStatus.canSend && (
              <Alert severity="error" sx={{ marginBottom: 1 }}>
                Cannot schedule messages for this day: You have reached your
                daily scheduled message limit of {schedulingStatus.sendingLimit}{' '}
                messages.
              </Alert>
            )}
          </Box>
        )}

        <FormBuilder
          config={[
            {
              type: 'group',
              items: [
                {
                  type: 'input',
                  name: 'scheduleDate',
                  label: 'Date',
                  input: 'date',
                  trackingName: 'schedule-date-input',
                  props: {
                    required: true,
                    inputType: 'date',
                    minDate: DateTime.now(),
                    maxDate: DateTime.now().plus({ days: 28 }),
                    shouldDisableDate: (date: DateTime) => {
                      // Disable weekends (Saturday = 6, Sunday = 7 in Luxon)
                      return date.weekday === 6 || date.weekday === 7;
                    },
                  },
                },
                {
                  type: 'input',
                  name: 'scheduleTime',
                  label: 'Time',
                  input: 'select',
                  trackingName: 'schedule-time-select',
                  props: {
                    options: startTimeOptions,
                    required: true,
                  },
                },
              ],
              size: 2,
            },
          ]}
          control={control}
        />
      </CompositeDialog>
    </TrackingProvider>
  );
}
